import { Holdings, PortfolioPayload, Security } from "~shapes/portfolio"
import PortfolioService from "~services/PortfolioService"

export const portfolioToObj = (portfolio: Holdings[]) => {
  const obj: Record<string, number> = {}
  if (portfolio.length === 0) return obj
  portfolio.forEach((item: Holdings) => {
    obj[item.security.code] = (obj[item.security.code] ?? 0) + item.quantity
  })
  return obj
}

// Maps the per-security form values back into holdings. Each security is
// treated independently — no grouping or zeroing of related securities.
export const hydratePortfolio = (
  portfolio: object,
  securities: Security[] = [],
): Holdings[] => {
  const secMap: Record<string, Security> = {}
  securities.forEach(s => {
    secMap[s.code] = s
  })

  const holdings: Holdings[] = []
  Object.keys(portfolio).forEach(code => {
    const quantity = parseFloat(portfolio[code])
    if (!(quantity > 0)) return

    const sec = secMap[code]
    if (sec) {
      holdings.push({
        security: {
          code,
          exchangeCode: sec.exchange,
          tickerSymbol: sec.symbol,
        },
        quantity,
      })
    } else {
      holdings.push({
        security: { code, exchangeCode: "", tickerSymbol: "" },
        quantity,
      })
    }
  })
  return holdings
}

// Builds the portfolio-creation payload, or returns null when there are no
// positive holdings. hydratePortfolio drops zero/blank/negative quantities, so
// an all-zero (or empty) form yields no holdings — and we must never POST a
// portfolio with an empty holdings list.
export const buildPortfolioPayload = (
  formData: { portfolio?: Record<string, unknown>; broker?: string },
  securities: Security[],
  meta: {
    userId: string
    fallbackBroker: string
    pooId: string
    date: string
  },
): PortfolioPayload | null => {
  const holdings = hydratePortfolio(formData.portfolio || {}, securities)
  if (holdings.length === 0) return null

  return {
    userId: meta.userId,
    broker: formData.broker || meta.fallbackBroker,
    holdings,
    poo: { id: meta.pooId, date: meta.date },
  }
}

// Builds the display label for a security input. The base is always
// "EXCHANGE.SYMBOL" (e.g. "NYSE.CCL"). When `showCode` is set, the catalog code
// is appended as a disambiguator — but only when it adds information, i.e. when
// the code is more than just "exchange-symbol". So "lse-ccl" is redundant for
// LSE.CCL and is omitted, while "nyse-ccl-2026" is kept for NYSE.CCL.
export const buildSecurityLabel = (
  sec: { exchange: string; symbol: string; code: string },
  showCode = true,
): string => {
  const base = `${sec.exchange}.${sec.symbol}`
  if (!showCode) return base

  const redundant = `${sec.exchange}-${sec.symbol}`.toLowerCase()
  if ((sec.code ?? "").toLowerCase() === redundant) {
    return base
  }
  return `${base} (${sec.code})`
}

export const existValidPortfolio = (portfolio: Holdings[]) => {
  return portfolio.filter(el => el.state === "VALID").length > 0
}

export const buildEmptyPortfolio = async (securities?: Security[]) => {
  try {
    let securitiesData = securities
    if (!securitiesData) {
      securitiesData = await PortfolioService.getSecurities()
    }
    return securitiesData.map(sec => ({
      security: {
        code: sec.code,
        exchangeCode: sec.exchange,
        tickerSymbol: sec.symbol,
      },
      quantity: 0,
    }))
  } catch (e) {
    return []
  }
}
